import Cookies from 'universal-cookie';
import store from '../redux/store';

/** Token de sesión tal y como lo leen ClientsView y SocietiesView. */
export const getAccessToken = (): string => {
  const cookies = new Cookies();
  const userCookie = cookies.get('101Obex');
  return userCookie?.token || '';
};

/**
 * Sociedad elegida en el header. Sin ella se lanza en vez de caer en una por
 * defecto: mejor un error que leer o contabilizar en una sociedad que nadie eligió.
 */
export const getActiveCompanyId = (): string => {
  const id = store.getState().activeCompany.activeId;
  if (!id) throw new Error('No hay sociedad activa seleccionada.');
  return id;
};

/** Query string a partir de un objeto; omite undefined, null y cadenas vacías. */
export const buildQuery = (params: Record<string, any>): string => {
  const parts: string[] = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return;
    parts.push(`${key}=${encodeURIComponent(String(value))}`);
  });
  return parts.length > 0 ? `?${parts.join('&')}` : '';
};

/** El gateway envuelve la respuesta en .data y/o .result de forma inconsistente. */
const unwrapEnvelope = (res: any): any => {
  let out = res;
  if (out && out.data) out = out.data;
  if (out && out.result) out = out.result;
  return out;
};

/** Extrae el array de una respuesta de listado, sea cual sea la forma que llegue. */
export const unwrapList = (res: any): any[] => {
  const out = unwrapEnvelope(res);
  if (out && out.response && Array.isArray(out.response.data)) return out.response.data;
  if (out && Array.isArray(out.response)) return out.response;
  if (out && Array.isArray(out.data)) return out.data;
  if (Array.isArray(out)) return out;
  return [];
};

/**
 * Fallo de transporte (HTTP 4xx/5xx de la pasarela, ruta no publicada, red):
 * RequestHandler no lanza, devuelve {success:false, status, message}. Sin esto
 * un listado fallido se confunde con un listado vacío.
 */
export const getTransportError = (res: any): string | null => {
  if (res && res.success === false && typeof res.message === 'string' && res.message !== '') return res.message;
  return null;
};

/** Listado paginado ({data, total, page, page_size}); si llega un array plano, total = su longitud. */
export const unwrapPage = (res: any): { rows: any[]; total: number; page: number; pageSize: number } => {
  const out = unwrapEnvelope(res);
  const payload = out && out.response !== undefined ? out.response : out;
  if (payload && Array.isArray(payload.data)) {
    return {
      rows:     payload.data,
      total:    Number(payload.total) || payload.data.length,
      page:     Number(payload.page) || 1,
      pageSize: Number(payload.page_size) || payload.data.length
    };
  }
  const rows = unwrapList(res);
  return { rows, total: rows.length, page: 1, pageSize: rows.length };
};

/** Extrae el registro de una respuesta de alta/modificación, o null si el backend devolvió un error. */
export const unwrapRecord = (res: any): any | null => {
  const out = unwrapEnvelope(res);
  const record = out && out.response !== undefined ? out.response : out;
  if (record && record.id !== undefined && record.id !== null) return record;
  return null;
};

/** Objeto de respuesta de un endpoint que devuelve un único objeto sin id (resúmenes, totales). */
export const unwrapRecordOrObject = (res: any): any | null => {
  const out = unwrapEnvelope(res);
  const record = out && out.response !== undefined ? out.response : out;
  return record && typeof record === 'object' && !Array.isArray(record) ? record : null;
};

/** Mensaje de error del backend: los .avap devuelven un string plano en response. */
export const unwrapError = (res: any, fallback: string): string => {
  const out = unwrapEnvelope(res);
  if (out && typeof out.response === 'string') return out.response;
  const transportError = getTransportError(res);
  if (transportError) return `${fallback} (${transportError})`;
  return fallback;
};
